import { useQuery } from '@tanstack/react-query'
import { useEffect, useId } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { StateView } from '@/card/record'
import { ApiError } from '@/lib/api'
import { formatMoney } from '@/format/money'
import { fetchOrganizations } from './api'
import type { Organization } from './types'
import '@/styles/payments.css'

/**
 * Общая обвязка экранов раздела «Оплата» (ТЗ §4.4) — тикет #78, вёрстка по BankRecon,
 * Cashbook и Fiscal (.dc.html).
 *
 * Три экрана делят одно и то же: выбор организации, состояния загрузки и отказа,
 * форматирование денег. Написать это трижды значило бы получить три немного разных ответа
 * на вопрос «что показать, когда права нет», — и разошлись бы они при первой правке.
 */

/** Организации — один запрос на все экраны раздела (тот же ключ кэша). */
export function useOrganizations() {
  return useQuery({
    queryKey: ['payment-organizations'],
    queryFn: ({ signal }) => fetchOrganizations(signal),
    retry: false,
  })
}

/**
 * Выбор юридического лица — сегментом, как в макете кассы.
 *
 * «Ровно одна организация в базе» — неверное допущение: их несколько по замыслу, у каждой
 * свой ИНН, свои счета и своя касса. Единственная организация выбирается сама (спрашивать
 * там, где выбора нет, — лишний клик), но при нескольких выбирает человек: подставленная
 * за него первая показала бы чужие деньги, и заметно это стало бы не сразу.
 *
 * Сегмент вмещает несколько юрлиц; дальше он перестаёт читаться, и выбор становится
 * обычным списком.
 */
export function OrganizationPicker({
  value,
  onChange,
}: {
  value: string | null
  onChange: (id: string | null) => void
}) {
  const { t } = useApp()
  const labelId = useId()
  const organizations = useOrganizations()
  const list = organizations.data ?? []

  useEffect(() => {
    const only = list.length === 1 ? list[0] : undefined
    if (!value && only) onChange(only.id)
  }, [value, list, onChange])

  // Отказ и сбой показывает экран целиком (`OrganizationsFailure`): выбор без списка
  // организаций — пустая рамка, которая выглядит как «организаций нет».
  if (organizations.error || list.length === 0) return null

  return (
    <div className="row pay-org">
      <span className="label" id={labelId}>
        {t('payments.organization')} <span className="req" aria-hidden="true">*</span>
      </span>
      {list.length <= 4 ? (
        <div className="seg" role="group" aria-labelledby={labelId}>
          {list.map((organization) => (
            <button
              key={organization.id}
              type="button"
              aria-pressed={organization.id === value}
              onClick={() => onChange(organization.id)}
            >
              <Icon name="building" size="sm" />
              {organization.name}
            </button>
          ))}
        </div>
      ) : (
        <select
          className="input input-sm"
          aria-labelledby={labelId}
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value || null)}
        >
          <option value="">{t('payments.organization.choose')}</option>
          {list.map((organization) => (
            <option key={organization.id} value={organization.id}>
              {organization.name}
            </option>
          ))}
        </select>
      )}
    </div>
  )
}

/**
 * Организация не выбрана — денег не показываем вовсе.
 *
 * Метка состояния — «Выберите организацию», а не «Нет данных»: данные есть, их просто
 * нельзя сложить. Общий остаток двух юрлиц — число, которого нет ни в одной бумаге.
 */
export function OrganizationRequired({
  organizations,
  onPick,
  titleKey,
  textKey,
}: {
  organizations: Organization[]
  onPick: (id: string) => void
  titleKey: string
  textKey: string
}) {
  const { t } = useApp()
  return (
    <div className="state-box">
      <div className="state is-setup pay-state" role="status">
        <span className="state-kind">{t('payments.organization.choose')}</span>
        <span className="state-ico">
          <Icon name="building" />
        </span>
        <span className="st">{t(titleKey)}</span>
        <span className="sd">{t(textKey)}</span>
        {organizations.length > 0 && (
          <span className="sa">
            {organizations.map((organization) => (
              <button
                key={organization.id}
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => onPick(organization.id)}
              >
                {organization.name}
              </button>
            ))}
          </span>
        )}
      </div>
    </div>
  )
}

/** Список организаций не получен: экран без него не может ответить «чьи деньги». */
export function OrganizationsFailure({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return <QueryFailure error={error} onRetry={onRetry} />
}

/**
 * Отказ запроса как состояние экрана.
 *
 * «Нет доступа» и «ошибка» — разные состояния с разными действиями: отказ по правам не
 * лечится повтором, сбой — лечится. Текст ошибки — серверный: он называет причину точнее
 * любого общего «что-то пошло не так».
 */
export function QueryFailure({
  error,
  onRetry,
  compact,
}: {
  error: unknown
  onRetry?: () => void
  compact?: boolean
}) {
  const { t } = useApp()
  const forbidden = error instanceof ApiError && error.isForbidden
  if (forbidden) {
    return (
      <StateView
        boxed={!compact}
        compact={compact}
        kind="forbidden"
        title={t('access.denied')}
        description={t('payments.forbidden.hint')}
      />
    )
  }
  return (
    <StateView
      boxed={!compact}
      compact={compact}
      kind="error"
      title={t('payments.error.title')}
      description={error instanceof ApiError ? error.message : t('app.error')}
      actions={
        onRetry ? (
          <button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>
            <Icon name="refresh" size="sm" />
            {t('payments.retry')}
          </button>
        ) : undefined
      }
      code={error instanceof ApiError ? String(error.status) : undefined}
    />
  )
}

/** Скелет таблицы на время загрузки: форма экрана видна раньше данных. */
export function TableSkeleton({ columns, rows = 6 }: { columns: string[]; rows?: number }) {
  const { t } = useApp()
  return (
    <div className="tbl-wrap" aria-busy="true" aria-label={t('app.loading')}>
      <table className="tbl">
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th key={index}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, row) => (
            <tr key={row}>
              {columns.map((_, cell) => (
                <td key={cell}>
                  <span className="skel" style={{ width: `${50 + ((row * 37 + cell * 23) % 60)}px` }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Каркас экрана: крошки, заголовок, действия, вводный абзац.
 *
 * Крошка «Платежи» — ссылка на реестр, только если реестр пользователю открыт: ссылка в
 * 403 — обещание, которого нет.
 */
export function PaymentsPage({
  titleKey,
  sub,
  head,
  actions,
  leadKey,
  children,
}: {
  titleKey: string
  sub?: ReactNode
  head?: ReactNode
  actions?: ReactNode
  leadKey?: string
  children: ReactNode
}) {
  const { t, user } = useApp()
  return (
    <div className="pay-page">
      <div className="crumbs">
        {can(user, 'payments.payment', 'read') ? (
          <Link to="/payments">{t('nav.section.payments')}</Link>
        ) : (
          <span>{t('nav.section.payments')}</span>
        )}
        <span>/</span>
        <span>{t(titleKey)}</span>
      </div>
      <div className="page-head">
        <h1 className="h1">{t(titleKey)}</h1>
        {sub}
        {head}
        <span className="spacer" />
        {actions}
      </div>
      {leadKey && <p className="page-lead">{t(leadKey)}</p>}
      {children}
    </div>
  )
}

/** Число из строки `numeric(19,4)`; нечисловое — ноль (для итогов, не для показа). */
export function amountOf(value: string | null | undefined): number {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : 0
}

/**
 * Денежная величина.
 *
 * Валюта печатается рядом с числом всегда, а не подразумевается: баланс ключуется
 * валютой, и цифра без неё на экране бухгалтера — это приглашение сложить рубли с евро.
 *
 * Приходит строкой, а не числом: `numeric(19,4)` не помещается в double без потерь, и
 * разбор в `number` на клиенте вернул бы копейки, которых нет ни в базе, ни в счёте.
 * Поэтому исходная строка остаётся в `title`.
 */
export function Money({
  value,
  currency,
}: {
  value: string | number | null | undefined
  currency?: string | null
}) {
  const { locale } = useApp()
  if (value == null || value === undefined || value === '') {
    return (
      <span className="muted" title="нет данных">
        —
      </span>
    )
  }
  // The string is formatted as a string (#91): no double on the way to the screen.
  return <span title={String(value)}>{formatMoney(value, currency ?? null, locale)}</span>
}

/**
 * Отказ действия — строкой над экраном.
 *
 * Отказ по правам показывается своим текстом: «нет доступа» и «сломалось» — разное.
 * Остальное — текстом сервера: он называет причину (`ledger.no_description`), а общий
 * «ошибка» отправил бы человека в поддержку с отказом, который он может исправить сам.
 */
export function Failure({ error }: { error: unknown }) {
  const { t } = useApp()
  const forbidden = error instanceof ApiError && error.isForbidden
  return (
    <div className="alert alert-danger" role="alert">
      <Icon name="alert" />
      <span className="fg">
        {forbidden ? t('access.denied') : error instanceof ApiError ? error.message : t('app.error')}
      </span>
    </div>
  )
}

/** Дата `YYYY-MM-DD…` в виде `09.10.2026`; время — отдельно, если оно есть. */
export function dateText(value: string | null | undefined): string {
  if (!value) return '—'
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value
}

/** Время `HH:MM` из ISO-момента в поясе браузера; пусто, если в значении только дата. */
export function timeText(value: string | null | undefined): string {
  if (!value || value.length <= 10) return ''
  const moment = new Date(value)
  if (Number.isNaN(moment.getTime())) return ''
  return moment.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}
